import { computed, reactive, ref, watch } from 'vue'
import { DriverService } from './driver'
import { DriverSocketMsg, DriverSocketMsgType } from '@libs/types/message'
import { DevicePid, KK } from '@/define/define'
import { getCurrentDeviceMagnetKKs } from '@/profiles/selector'
import { DataDeadZone, PostMagnetSetKeyDeadZone, WatchDevice } from '@libs/types/response'
import { DeviceManager, DeviceProtocol } from '../device'
import { OperateType, operate } from './operate'
import { KeyGroup, MagnetKeyType, SpecificItem, SpecificItemAction } from '@libs/types/enum'
import { magnetSetKeyGroup } from './magnet_key_group'
import { arraysEqual } from '@/tool/utility'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { setData } from './data'
import firmware from '@/upgrade/firmware'
import { handleGetTravel } from '@/device_tweaks/travel'
import { Fe87v2DeviceList } from '@/define/device_vendor'
import { convertDeadZoneFromReceive_Above6800, convertTravelFromReceive_Above6800, convertDeadZoneToSend_Above6800, convertTravelToSend_Above6800 } from './travel_converter'
import { adjustableKeys } from '@libs/define/adjustable_keys'
import { enableDialog } from '@/components/cdialog/dialog'
import i18n from '@/languages/i18n'
import { isFE87V2IMCNH } from '@/components/mgx_set/IMC_switch.vue'
const { t } = i18n.global

export interface MagnetKeyInfo {
  PressNum: number
  ReleaseNum: number
  QuickTriggerPressNum: number
  QuickTriggerReleaseNum: number
}

export interface PostMagnetSetKeyInfo extends MagnetKeyInfo {
  Keys: number[]
}

export interface GetMagnetKeyInfo extends MagnetKeyInfo {
  Key: number
  KeyType: MagnetKeyType
}

export interface PostMagnetKeyInfo {
  Keys: number[]
}

export interface MagnetDynamicReport {
  Key: KK
  Value: number
}

class MagnetSet {
  keysInfoReading = ref(true)
  dataCleared = ref(false)

  globalTravel = reactive({
    globalPressNum: 0,
    globalQuickTriggerPressNum: 0,
    globalQuickTriggerReleaseNum: 0,
  })

  magnetData = reactive({
    pressNum: 0,
    quickTriggerPressNum: 0,
    quickTriggerReleaseNum: 0,
  })

  list: { [index: number]: GetMagnetKeyInfo } = reactive({})
  selectedKeys = ref<KK[]>([])
  selectedKeySet = computed(() => new Set(this.selectedKeys.value))
  _showGuideFlag = ref(false) // INTERNAL: 用于通知 device_wrapper 显示虚拟键盘选键提示

  checkKeysInfoTask: NodeJS.Timeout | null = null

  deadZone = ref<number[]>([0, 0])

  newDeadZone = reactive<DataDeadZone>({ Top: 0, Bottom: 0 })
  globalDeadZone = reactive<DataDeadZone>({ Top: 0, Bottom: 0 })
  deadZoneList: { [key: KK]: DataDeadZone } = reactive({})

  constructor() {
    this.watchSelectedKeys()
    this.initIsShowSingleKeyDeadZone()
  }

  changelist(data: PostMagnetSetKeyInfo) {
    data.Keys.forEach((item) => {
      if (this.list[item]) {
        this.list[item].PressNum = data.PressNum
        this.list[item].ReleaseNum = data.ReleaseNum
        this.list[item].QuickTriggerReleaseNum = data.QuickTriggerReleaseNum
        this.list[item].QuickTriggerPressNum = data.QuickTriggerPressNum
      }
    })
  }

  watchKeysTrip() {
    let keys = getCurrentDeviceMagnetKKs.value
    if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
      keys = adjustableKeys(DeviceManager.instance.state.currentPid)
    }
    this.watchKeyTrip(keys)
  }

  cancelWatchKeysTrip() {
    if (DeviceManager.instance.state.driverProtocol == DeviceProtocol.dll_exe && DeviceManager.instance.state.currentPid != DevicePid.None) {
      this.watchKeyTrip([])
    }
  }

  private watchKeyTrip(keys: KK[]) {
    const data = {
      Type: DriverSocketMsgType.MTMagnetSetWatch,
      Data: { Keys: keys },
    } as DriverSocketMsg<any>
    DriverService.instance.askTypeAndData(data)
  }

  async getDeadZone() {
    const res: number[] = await this.askDeadZone(null, SpecificItemAction.Read)
    this.deadZone.value = res
  }
  async setDeadZone(value: number[]) {
    const res: number[] = await this.askDeadZone(value, SpecificItemAction.Write)
    this.deadZone.value = res
  }

  async askDeadZone(value: number[] | null, Action: SpecificItemAction): Promise<number[]> {
    const data = {
      Type: DriverSocketMsgType.MTSetOrReadSpecificItem,
      Data: {
        Item: SpecificItem.DeadZone,
        Action: Action,
        Value: value,
      },
    } as DriverSocketMsg<any>
    return await DriverService.instance.askTypeAndData<number[]>(data)
  }
  setKeyInfo(info: PostMagnetSetKeyInfo) {
    info = convertTravelToSend_Above6800(info)
    const data = {
      Type: DriverSocketMsgType.MTMagnetSetKeyInfo,
      Data: info,
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData(data)
  }

  handelKeysInfo() {
    this.keysInfoReading.value = true
    this.checkKeysInfo()
    this.getKeysInfo()
  }

  getKeysInfo(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      // if (Object.keys(this.list).length == 0) {
      //   await delay(800)
      // }
      let keys = getCurrentDeviceMagnetKKs.value
      if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
        keys = adjustableKeys(DeviceManager.instance.state.currentPid)
      }
      try {
        let da = await this.getKeyInfo<GetMagnetKeyInfo[]>(keys)
        // console.log('getKeyTravel', da)
        this.keysInfoReading.value = false
        da = await this.feV2FW212CheckTravel(da)
        await this.feV2FWNHCheckTravel(da)
        da = convertTravelFromReceive_Above6800(da)
        const res = handleGetTravel(da)

        this.reduceList(res)
        res.forEach((item, _) => {
          this.list[item.Key] = item
        })
        resolve()
      } catch (e) {
        reject(e)
      }
    })
  }

  private checkKeysInfo() {
    this.checkKeysInfoTask = setTimeout(() => {
      if (this.keysInfoReading.value == true && Object.values(this.list).length == 0) {
        DriverService.instance.killDriverService()
      }
    }, 3000)
  }

  private reduceList(list: GetMagnetKeyInfo[]) {
    const data = list.reduce((acc: { [index: string]: GetMagnetKeyInfo[] }, item) => {
      const { PressNum, QuickTriggerReleaseNum, QuickTriggerPressNum } = item
      const key = PressNum + '_' + QuickTriggerReleaseNum + '_' + QuickTriggerPressNum
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }, {})

    let dataList = Object.values(data)
    dataList.sort((a, b) => {
      return b.length - a.length
    })

    if (dataList.length > 0 && dataList[0] && dataList[0].length > 0) {
      this.globalTravel.globalPressNum = dataList[0][0].PressNum
      this.globalTravel.globalQuickTriggerPressNum = dataList[0][0].QuickTriggerPressNum
      this.globalTravel.globalQuickTriggerReleaseNum = dataList[0][0].QuickTriggerReleaseNum

      if (magnetSet.selectedKeys.value.length == 0) {
        this.magnetData.pressNum = dataList[0][0].PressNum
        this.magnetData.quickTriggerReleaseNum = dataList[0][0].QuickTriggerReleaseNum
        this.magnetData.quickTriggerPressNum = dataList[0][0].QuickTriggerPressNum
      }
    }
  }

  getKeyInfo<T>(keys: KK[]): Promise<T> {
    const data = {
      Type: DriverSocketMsgType.MTMagnetGetKeyInfo,
      Data: { Keys: keys } as PostMagnetKeyInfo,
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData<T>(data)
  }

  async getKeysDeadZone() {
    let res = await DriverService.instance.askType<{ [key: KK]: DataDeadZone }>(DriverSocketMsgType.MTMagnetGetKeyDeadZone)
    // console.log('getKeysDeadZone', res)
    res = await this.feV2FW212CheckDeadZone(res)
    res = convertDeadZoneFromReceive_Above6800(res)
    this.deadZoneList = res
    const resValues = Object.values(res)
    this.deadZone.value = [resValues[0].Top, resValues[0].Bottom]
    this.setGlobalDeadZone(res)
  }

  // fe87v2 2.1.2及以上版本死区如果有小于0.3的，则全部设置为0.3
  async feV2FW212CheckDeadZone(res: { [key: KK]: DataDeadZone }) {
    if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
      const fwGT212 = await firmware.fwVersionGreaterThanOrEqual('2.1.2', [...Fe87v2DeviceList])
      if (fwGT212) {
        let changeKKs: KK[] = []
        for (const key in res) {
          if (res[key].Top < 300) {
            changeKKs.push(Number(key))
          }
        }
        if (changeKKs.length > 1) {
          const data = {
            Keys: changeKKs,
            Top: 300,
          } as PostMagnetSetKeyDeadZone

          try {
            await this.setKeysDeadZone(data)
            return await DriverService.instance.askType<{ [key: KK]: DataDeadZone }>(DriverSocketMsgType.MTMagnetGetKeyDeadZone)
          } catch (error) {
            console.log(error)
            enableDialog(t('Initialization_failed'))
          }
        }
        return res
      }
    }
    return res
  }

  // fe87v2 2.1.2及以上版本行程如果有小于0.3的，则全部设置为0.3
  async feV2FW212CheckTravel(list: GetMagnetKeyInfo[]) {
    if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
      const fwGT212 = await firmware.fwVersionGreaterThanOrEqual('2.1.2', [...Fe87v2DeviceList])
      if (fwGT212) {
        let changeKKs: KK[] = []
        list = list.map((item) => {
          if (item.PressNum < 300) {
            changeKKs.push(item.Key)
          }
          return item
        })
        if (changeKKs.length > 1) {
          const data = {
            Keys: changeKKs,
            PressNum: 300,
            ReleaseNum: 300,
          } as PostMagnetSetKeyInfo

          try {
            await this.setKeyInfo(data)
            const keys = adjustableKeys(DeviceManager.instance.state.currentPid)
            return await this.getKeyInfo<GetMagnetKeyInfo[]>(keys)
          } catch (error) {
            console.log(error)
            enableDialog(t('Initialization_failed'))
          }
        }
        return list
      }
    }
    return list
  }

  // fe87v2 NH 行程如果有小于1.5的，则全部设置为1.5
  async feV2FWNHCheckTravel(list: GetMagnetKeyInfo[]) {
    if (specialCheckCurrent(speciaBehiver.DevicesOfSelfDevMix)) {
      if (isFE87V2IMCNH()) {
        let changeKKs: KK[] = []
        list = list.map((item) => {
          if (item.PressNum < 1500) {
            changeKKs.push(item.Key)
          }
          return item
        })
        if (changeKKs.length > 1) {
          const data = {
            Keys: changeKKs,
            PressNum: 1500,
            ReleaseNum: 1500,
          } as PostMagnetSetKeyInfo

          try {
            await this.setKeyInfo(data)
          } catch (error) {
            console.log(error)
            enableDialog(t('Initialization_failed'))
          }
        }
      }
    }
  }

  private setGlobalDeadZone(res: { [key: KK]: DataDeadZone }) {
    const deadZoneData = Object.values(res).reduce(
      (acc: { [index: string]: DataDeadZone[] }, item: DataDeadZone) => {
        const key = item.Top + '_' + item.Bottom
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(item)
        return acc
      },
      {} as { [index: string]: DataDeadZone[] },
    )

    let dataList = Object.values(deadZoneData)
    dataList.sort((a, b) => {
      return b.length - a.length
    })
    if (dataList.length > 0 && dataList[0]) {
      this.globalDeadZone.Top = dataList[0][0].Top
      this.globalDeadZone.Bottom = dataList[0][0].Bottom

      if (this.selectedKeys.value.length == 0) {
        this.newDeadZone.Top = dataList[0][0].Top
        this.newDeadZone.Bottom = dataList[0][0].Bottom
      }
    }
  }

  setKeysDeadZone(setData: PostMagnetSetKeyDeadZone) {
    setData = convertDeadZoneToSend_Above6800(setData)
    const data = {
      Type: DriverSocketMsgType.MTMagnetSetKeyDeadZone,
      Data: setData,
    } as DriverSocketMsg<any>
    return DriverService.instance.askTypeAndData(data)
  }

  private watchSelectedKeys() {
    watch(this.selectedKeys, (values, oldValues) => {
      for (const [key, value] of magnetSetKeyGroup.state.keysGroup.entries()) {
        const fvs = value.filter((kk) => this.checkKeyIsSetMagnet(kk))
        const kk = Array.from(new Set([...values, ...fvs]))

        if (arraysEqual(value, kk) && values.length > 0) {
          magnetSetKeyGroup.state.keysGroupType = key
          break
        }
        magnetSetKeyGroup.state.keysGroupType = KeyGroup.none
      }

      if (values.length < oldValues.length) {
        this.changeViewByCancelKeys(values, oldValues)
      }
      if (values.length > oldValues.length) {
        this.changeViewByAddKeys(values)
      }
    })
  }

  // 检查是否设置高级改键
  private checkKeyIsSetMagnet(kk: KK): boolean {
    if (specialCheckCurrent(speciaBehiver.DevicesOfWitmod)) {
      if (setData.isDKS(kk)) return true
      return false
    }
    if (!this.list[kk]) return false

    if (this.list[kk].KeyType > 0) {
      return true
    }
    return false
  }

  // 取消按键
  private changeViewByCancelKeys(values: number[], _oldValues: number[]) {
    if (values.length == 0) {
      this.magnetData.pressNum = this.globalTravel.globalPressNum
      this.magnetData.quickTriggerPressNum = this.globalTravel.globalQuickTriggerPressNum
      this.magnetData.quickTriggerReleaseNum = this.globalTravel.globalQuickTriggerReleaseNum

      this.newDeadZone.Top = this.globalDeadZone.Top
      this.newDeadZone.Bottom = this.globalDeadZone.Bottom
    } else if (values.length > 0) {
      this.magnetData.pressNum = this.list[values[0]].PressNum
      this.magnetData.quickTriggerPressNum = this.list[values[0]].QuickTriggerPressNum
      this.magnetData.quickTriggerReleaseNum = this.list[values[0]].QuickTriggerReleaseNum

      this.fwIsSupportSingleKeyDeadZone().then((res: boolean) => {
        if (res) {
          this.newDeadZone.Top = this.deadZoneList[values[0]].Top
          this.newDeadZone.Bottom = this.deadZoneList[values[0]].Bottom
        }
      })
    }
  }

  async fwIsSupportSingleKeyDeadZone() {
    if (specialCheckCurrent(speciaBehiver.SupportSingleKeyDeadZone)) {
      return true
    }
    const fwGTE107 = await firmware.fwVersionGreaterThanOrEqual('1.0.7', [DevicePid.IROKNa87Pro, DevicePid.IYXMu68Pro, DevicePid.IROKNd63])
    const fwGTE113 = await firmware.fwVersionGreaterThanOrEqual('1.1.3', [DevicePid.IROKNd63_cyan, DevicePid.IYXMu68Pro_cyan])
    const fwGTE103 = await firmware.fwVersionGreaterThanOrEqual('1.0.3', [DevicePid.IYXMu68_ULTRA])
    const fwGTE20250326 = await firmware.versionIs20250326()
    return fwGTE107 || fwGTE113 || fwGTE103 || fwGTE20250326
  }

  public changeViewByAddKeys(values: number[]) {
    if (values.length == getCurrentDeviceMagnetKKs.value.length) {
      this.magnetData.pressNum = this.globalTravel.globalPressNum
      this.magnetData.quickTriggerPressNum = this.globalTravel.globalQuickTriggerPressNum
      this.magnetData.quickTriggerReleaseNum = this.globalTravel.globalQuickTriggerReleaseNum
    } else {
      let key = values[0]
      this.magnetData.pressNum = this.list[key].PressNum
      this.magnetData.quickTriggerPressNum = this.list[key].QuickTriggerPressNum
      this.magnetData.quickTriggerReleaseNum = this.list[key].QuickTriggerReleaseNum
    }

    this.fwIsSupportSingleKeyDeadZone().then((res: boolean) => {
      if (res && this.deadZoneList[values[0]]) {
        this.newDeadZone.Top = this.deadZoneList[values[0]].Top
        this.newDeadZone.Bottom = this.deadZoneList[values[0]].Bottom
      }
    })
  }

  clearMagnetSet(data: WatchDevice | null) {
    if (this.dataCleared.value) {
      return
    }
    if (data && data.In) {
      return
    }

    const device = DeviceManager.instance.state.deviceList.filter((e) => e.ID == data?.Device)
    if (device.length > 0 && device[0].IsSupportMagnet == false) {
      return
    }

    if (this.checkKeysInfoTask != null) {
      clearTimeout(this.checkKeysInfoTask)
    }

    for (const key in this.list) {
      delete this.list[key]
    }

    this.dataCleared.value = true

    this.selectedKeys.value.length = 0
    this.keysInfoReading.value = true
    this.globalTravel.globalPressNum = 0
    this.globalTravel.globalQuickTriggerReleaseNum = 0
    this.globalTravel.globalQuickTriggerPressNum = 0
    this.magnetData.pressNum = 0
    this.magnetData.quickTriggerPressNum = 0
    this.magnetData.quickTriggerReleaseNum = 0
    magnetSetKeyGroup.clear()

    if (data && !data.In && data.Device == DeviceManager.instance.state.currentPid) {
      operate.changeActionType(OperateType.none)
      return
    }
    this.cancelWatchKeysTrip()
  }

  isShowSingleKeyDeadZone = ref(false)

  enableSingleKeyDeadZone = () => {
    this.isShowSingleKeyDeadZone.value = !this.isShowSingleKeyDeadZone.value
    localStorage.setItem('isShowSingleKeyDeadZone', this.isShowSingleKeyDeadZone.value.toString())
  }

  initIsShowSingleKeyDeadZone = () => {
    const isShowSingleKeyDeadZoneStr = localStorage.getItem('isShowSingleKeyDeadZone')
    if (isShowSingleKeyDeadZoneStr != null) {
      this.isShowSingleKeyDeadZone.value = isShowSingleKeyDeadZoneStr == 'true'
    }
  }
}

let magnetSet = new MagnetSet()
export { magnetSet }
